import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { fetchLoomRebuildStatus, type LoomRebuildStatus } from "../../api/loomRebuild";
import type { LoomAuthoringDocument } from "./loomAuthoringModel";
import { loomAuthoringDocumentStateId } from "./loomAuthoringHistory";
import {
  initialLoomAuthoritativeRebuildRuntime,
  LoomAuthoritativeRebuildScheduler,
} from "./loomAuthoritativeRebuildScheduler";
import type { LoomNativeHybridAsset } from "./loomNativeHybridAsset";

const AUTOMATIC_REBUILD_SETTLE_MS = 1_200;

export function useLoomAuthoritativeRebuild(
  projectId: string,
  document: LoomAuthoringDocument,
  asset: LoomNativeHybridAsset | null,
  automatic: boolean,
  previewActive: boolean,
) {
  const [runtime, setRuntime] = useState(initialLoomAuthoritativeRebuildRuntime);
  const [serviceStatus, setServiceStatus] = useState<LoomRebuildStatus | null>(null);
  const [settlingRevision, setSettlingRevision] = useState<number | null>(null);
  const automaticRequestTimer = useRef<number | null>(null);
  const scheduler = useMemo(
    () => new LoomAuthoritativeRebuildScheduler(
      projectId, setRuntime, undefined, fetchLoomRebuildStatus,
    ),
    [projectId],
  );
  useEffect(() => {
    scheduler.activate();
    return () => scheduler.dispose();
  }, [scheduler]);
  useEffect(() => {
    const controller = new AbortController();
    fetchLoomRebuildStatus(projectId, controller.signal).then(setServiceStatus).catch(() => setServiceStatus(null));
    return () => controller.abort();
  }, [projectId]);
  const stateId = loomAuthoringDocumentStateId(document);
  scheduler.observe(document);
  useEffect(() => {
    if (previewActive) {
      setSettlingRevision(null);
      return undefined;
    }
    if (!automatic) {
      setSettlingRevision(null);
      scheduler.synchronize(document);
      return undefined;
    }
    if (!asset || serviceStatus?.configured !== true) {
      setSettlingRevision(null);
      return undefined;
    }
    setSettlingRevision(document.revision);
    const timer = window.setTimeout(() => {
      if (automaticRequestTimer.current === timer) automaticRequestTimer.current = null;
      setSettlingRevision((current) => current === document.revision ? null : current);
      scheduler.request(document, asset);
    }, AUTOMATIC_REBUILD_SETTLE_MS);
    automaticRequestTimer.current = timer;
    return () => {
      window.clearTimeout(timer);
      if (automaticRequestTimer.current === timer) automaticRequestTimer.current = null;
    };
  }, [asset, automatic, document.revision, previewActive, scheduler, serviceStatus?.configured, stateId]);
  const rebuild = useCallback(() => {
    if (automaticRequestTimer.current !== null) {
      window.clearTimeout(automaticRequestTimer.current);
      automaticRequestTimer.current = null;
    }
    setSettlingRevision(null);
    if (asset) scheduler.request(document, asset);
  }, [asset, document, scheduler]);
  const cancel = useCallback(() => {
    if (automaticRequestTimer.current !== null) {
      window.clearTimeout(automaticRequestTimer.current);
      automaticRequestTimer.current = null;
    }
    setSettlingRevision(null);
    scheduler.cancel();
  }, [scheduler]);
  return { ...runtime, settlingRevision, serviceStatus, rebuild, cancel };
}
